/**
 * MapView — renders the active subset of the master 60-territory world map
 * for a given player count.
 *
 * Visual layers (z-order, bottom → top):
 *
 *   1. Slate ocean (TransformComponent background)
 *   2. Latitude/longitude graticule (faint, full canvas)
 *   3. Continent silhouettes (sea-foam halo + paper-cream wash + chalk coast)
 *      and their `<clipPath>` defs
 *   4. Adjacency: faint land-border ghosts (under territories) and bold
 *      dashed sea-route shipping lanes (over them)
 *   5. Territory polygons (Voronoi cells clipped to continent silhouettes)
 *      coloured by module tint or owner pigment, with hover/source/target
 *      affordances
 *   6. Cube count badges + hover-revealed territory labels
 *   7. Decorative furniture: continent name watermarks, compass rose,
 *      title cartouche, scale bar
 *
 * Production data binding (unchanged):
 *   - Cube counts come from `gameState.territories[id].cubeCount` (live).
 *   - Territory fill is the controlling player's pigment when present;
 *     unowned territories fall back to the module tint.
 *   - Click handling routes through the gameStore's combat dispatchers
 *     based on the current `attackState.kind`.
 */

import { type ReactNode, useMemo, useState, useEffect } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import {
  buildActiveMap,
  TERRITORY_TO_CONTINENT,
  type ContinentId,
  type ModuleId,
  type PlayerCount,
  type Territory,
  type TerritoryId,
} from '@qd/map';
import type { AttackState, GameState, PlayerColorKey, PlayerId } from '@qd/protocol';
import { playerColors, surfaces, fontStacks } from '@qd/design-tokens';
import { AdjacencyLines } from './AdjacencyLines.js';
import { AttackArrow } from './AttackArrow.js';
import { AttackBanner } from './AttackBanner.js';
import { Continents } from './Continents.js';
import { Graticule } from './MapDecorations.js';
import { CONTINENT_OFFSETS, getOffsetCentroid } from './MapOffsets.js';
import { useCubeDeltas } from '../../hooks/useCubeDeltas';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { CubeDelta } from '../play/CubeDelta';

/** Stable mapping of QA module → background tint, drawn from classic Risk board palette. */
const MODULE_FILL: Readonly<Record<ModuleId, string>> = {
  frontend: '#eab308',   // Yellow (North America)
  devops: '#ef4444',     // Red (South America)
  compliance: '#3b82f6', // Blue (Europe)
  security: '#f97316',   // Orange (Africa)
  backend: '#22c55e',    // Green (Asia)
  database: '#a855f7',   // Purple (Australia/Oceania)
};

/** Module fill opacity increased for vibrant board-game look. */
const MODULE_FILL_OPACITY = 0.85;
/** Owned territories paint with their controller's pigment at higher saturation. */
const OWNED_FILL_OPACITY = 0.62;


interface MapViewProps {
  readonly playerCount: PlayerCount;
  readonly gameState: GameState | null;
  readonly selfId: PlayerId | null;
  readonly onSelectSource: (id: TerritoryId) => void;
  readonly onSelectTarget: (id: TerritoryId) => void;
  readonly onEndCombat?: () => void;
  readonly showAdjacencyLines?: boolean;
  /**
   * Click semantics. Defaults to `'attack'` so existing call-sites are
   * unchanged. When `'deploy'`, tapping an owned territory fires
   * `onDeploy` (1 reserve cube) instead of `onSelectSource`. Used by
   * the rescue-deploy UI in Play.tsx when the player is stuck.
   */
  readonly mode?: 'attack' | 'deploy' | undefined;
  /** Required when `mode === 'deploy'`. Tap on owned territory fires this. */
  readonly onDeploy?: ((id: TerritoryId) => void) | undefined;
}

const MAP_VIEWBOX = '0 0 1024 792';

export function MapView({
  playerCount,
  gameState,
  selfId,
  onSelectSource,
  onSelectTarget,
  showAdjacencyLines = true,
  mode = 'attack',
  onDeploy,
  onEndCombat,
}: MapViewProps): ReactNode {
  const map = useMemo(() => buildActiveMap(playerCount), [playerCount]);
  const [hoveredId, setHoveredId] = useState<TerritoryId | null>(null);

  // O(1) by-id lookup so the attack arrow + banner can resolve centroids
  // and display names without scanning `map.territories` every render.
  const territoryById = useMemo(() => {
    const m = new Map<string, Territory>();
    for (const t of map.territories) m.set(t.id, t);
    return m;
  }, [map]);

  const centroidById = useMemo(() => {
    const m = new Map<TerritoryId, { readonly x: number; readonly y: number }>();
    for (const t of map.territories) m.set(t.id, t.centroid);
    return m;
  }, [map]);

  const selfPlayerReserves = selfId
    ? gameState?.players.find((p) => p.id === selfId)?.reserveCubes ?? 0
    : 0;

  const { deltas, dismiss } = useCubeDeltas();

  // Build O(1) lookups from gameState for the inner shape components.
  const ownerByTerritory = useMemo(() => {
    if (!gameState) return new Map<string, PlayerId>();
    const m = new Map<string, PlayerId>();
    for (const t of Object.values(gameState.territories)) {
      if (t.controllingPlayerId) m.set(t.territoryId, t.controllingPlayerId);
    }
    return m;
  }, [gameState]);

  const colorByPlayer = useMemo(() => {
    if (!gameState) return new Map<PlayerId, PlayerColorKey>();
    return new Map(gameState.players.map((p) => [p.id, p.color] as const));
  }, [gameState]);

  const cubeCounts = useMemo(() => {
    if (!gameState) return new Map<string, number>();
    const m = new Map<string, number>();
    for (const t of Object.values(gameState.territories)) {
      m.set(t.territoryId, t.cubeCount);
    }
    return m;
  }, [gameState]);

  // Group active territories by continent so each group can render inside
  // its continent's clipPath — that's how Voronoi cells get trimmed to a
  // real coastline silhouette.
  const territoriesByContinent = useMemo(() => {
    const out = new Map<ContinentId, Territory[]>();
    for (const t of map.territories) {
      const cont = TERRITORY_TO_CONTINENT[t.id] as ContinentId | undefined;
      if (!cont) continue;
      let arr = out.get(cont);
      if (!arr) {
        arr = [];
        out.set(cont, arr);
      }
      arr.push(t);
    }
    return out;
  }, [map]);

  const activeModuleIds = useMemo(
    () => new Set<ModuleId>(map.modules.map((m) => m.id)),
    [map],
  );

  const attackState: AttackState = gameState?.attackState ?? { kind: 'idle' };
  const sourceId =
    attackState.kind === 'sourceSelected' ||
    attackState.kind === 'targetSelected' ||
    attackState.kind === 'combatActive' ||
    attackState.kind === 'combatResolved' ||
    attackState.kind === 'territoryConquered'
      ? attackState.sourceId
      : null;
  // In attack mode the cream/wax halo highlights enemy territories the
  // selected source can hit. In deploy mode (rescue) the same halo
  // mechanic is reused to highlight ALL of the player's OWN territories
  // — those are the legal "where do I drop this rescue cube?" targets.
  const validTargets =
    mode === 'deploy' && gameState && selfId
      ? new Set(
          Object.values(gameState.territories)
            .filter((t) => t.controllingPlayerId === selfId)
            .map((t) => t.territoryId),
        )
      : attackState.kind === 'sourceSelected'
        ? new Set(attackState.validTargets)
        : new Set<string>();
  const selectedTargetId =
    attackState.kind === 'targetSelected' ||
    attackState.kind === 'combatActive' ||
    attackState.kind === 'combatResolved' ||
    attackState.kind === 'territoryConquered'
      ? attackState.targetId
      : null;

  // Arrow + banner geometry. The committed arrow is only drawn while
  // `attackState.kind === 'targetSelected'` — once combat starts, the
  // modal takes over and Play.tsx hides the map subtree, so leaving the
  // committed-arrow guard tight keeps the rules truthful even if a
  // future caller forgets to unmount us.
  const sourceTerritory = sourceId ? (territoryById.get(sourceId) ?? null) : null;
  const selectedTargetTerritory = selectedTargetId
    ? (territoryById.get(selectedTargetId) ?? null)
    : null;

  // Hover preview: while the user is in `sourceSelected` and hovers a
  // valid target, draw a faint cream "you'd attack into here" line so
  // intent is obvious before they commit.
  const previewTargetTerritory =
    attackState.kind === 'sourceSelected' &&
    hoveredId !== null &&
    validTargets.has(hoveredId)
      ? (territoryById.get(hoveredId) ?? null)
      : null;

  // Banner needs human-readable territory names.
  const sourceName = sourceTerritory?.displayName ?? null;
  const bannerTargetName =
    attackState.kind === 'targetSelected'
      ? (selectedTargetTerritory?.displayName ?? null)
      : null;

  const handleClick = (id: TerritoryId) => {
    if (!gameState || !selfId) return;
    const t = gameState.territories[id];
    if (!t) return;
    const owned = t.controllingPlayerId === selfId;

    // Rescue-deploy mode: tap your own territory to drop a reserve cube.
    // Other territories are inert. We only enter this mode from Play.tsx
    // when the player is stuck (no legal attack) AND has reserves.
    if (mode === 'deploy') {
      if (owned && onDeploy) onDeploy(id);
      return;
    }

    if (attackState.kind === 'idle') {
      if (owned && t.cubeCount > 1) onSelectSource(id);
      return;
    }
    if (attackState.kind === 'sourceSelected') {
      if (validTargets.has(id)) {
        onSelectTarget(id);
        return;
      }
      // Clicking the source territory again always cancels the selection.
      if (id === attackState.sourceId) {
        if (onEndCombat) onEndCombat();
        return;
      }
      // Clicking any other owned territory: re-anchor if it has enough cubes,
      // otherwise cancel the selection so the player isn't stuck forced to
      // pick a target.
      if (owned) {
        if (t.cubeCount > 1) {
          onSelectSource(id);
        } else {
          // 1-cube territory: can't attack from here — treat as cancel.
          if (onEndCombat) onEndCombat();
        }
        return;
      }
      // Clicking a non-owned, non-valid territory: cancel selection.
      if (onEndCombat) onEndCombat();
      return;
    }
    // For all other kinds, clicks are inert (combat is mid-flight).
  };

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onClick={(e) => {
        // Ocean click (the div background) cancels selection.
        if (e.target === e.currentTarget && onEndCombat) {
          onEndCombat();
        }
      }}
    >
      <TransformWrapper
        minScale={0.6}
        maxScale={2.5}
        wheel={{ step: 0.3 }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{
            width: '100%',
            height: '100%',
            background: surfaces.slate,
          }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          <svg
            viewBox={MAP_VIEWBOX}
            width="100%"
            height="100%"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
            preserveAspectRatio="xMidYMid meet"
            onClick={(e) => {
              // Clicking the SVG background (not a territory) cancels selection.
              if (e.target === e.currentTarget && onEndCombat) {
                onEndCombat();
              }
            }}
          >
          <defs>
            <filter id="qd-filter-geo-roughen" x="-20%" y="-20%" width="140%" height="140%">
              {/* Organic coastline wobble */}
              <feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="3" seed="2" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.5" xChannelSelector="R" yChannelSelector="G" result="displaced" />
              {/* Thick drop-shadow mimicking puzzle/board piece thickness */}
              <feDropShadow in="displaced" dx="0" dy="4" stdDeviation="3" floodColor="#000000" floodOpacity="0.5" />
            </filter>
          </defs>
          {/* Layer 2 — graticule beneath continents */}
          <Graticule />

          {/* Layer 3 — continent silhouettes + clipPath defs */}
          <Continents activeModuleIds={activeModuleIds} />

          {/* Layer 4a — faint land border ghosts (under territories), 4b sea routes drawn after */}
          {showAdjacencyLines ? <AdjacencyLines map={map} /> : null}

          {/* Layer 5 — territory polygons, grouped per continent and clipped */}
          {Array.from(territoriesByContinent.entries()).map(([continentId, territories]) => {
            const offset = CONTINENT_OFFSETS[continentId as ContinentId] || { dx: 0, dy: 0 };
            return (
              <g
                key={`territories-${continentId}`}
                clipPath={`url(#qd-continent-clip-${continentId})`}
                transform={`translate(${offset.dx}, ${offset.dy})`}
              >
                {territories.map((territory) => {
                const ownerId = ownerByTerritory.get(territory.id);
                const ownerColor = ownerId ? colorByPlayer.get(ownerId) : null;
                const fill = ownerColor
                  ? playerColors[ownerColor].hex
                  : MODULE_FILL[territory.moduleId];
                const fillOpacity = ownerColor ? OWNED_FILL_OPACITY : MODULE_FILL_OPACITY;
                return (
                  <TerritoryShape
                    key={territory.id}
                    territory={territory}
                    fill={fill}
                    fillOpacity={fillOpacity}
                    isHovered={territory.id === hoveredId}
                    isSource={territory.id === sourceId}
                    isValidTarget={validTargets.has(territory.id)}
                    isSelectedTarget={territory.id === selectedTargetId}
                    onHover={setHoveredId}
                    onClick={handleClick}
                  />
                );
              })}
            </g>
          )})}

          {/* Layer 6 — cube badges + labels (NOT clipped, so badges near a
              coastline don't get sliced in half) */}
          <g data-layer="badges">
            {map.territories.map((territory) => {
              const count = cubeCounts.get(territory.id) ?? 0;
              if (count <= 0) return null;
              const centroid = getOffsetCentroid(territory.id, territory.centroid);
              const ownerId = ownerByTerritory.get(territory.id);
              const ownerColorKey = ownerId ? colorByPlayer.get(ownerId) : null;
              const ownerColorHex = ownerColorKey ? playerColors[ownerColorKey].hex : null;
              const isSelf = selfId !== null && ownerId === selfId;
              return (
                <CubeBadge
                  key={`badge-${territory.id}`}
                  cx={centroid.x}
                  cy={centroid.y}
                  count={count}
                  ownerColor={ownerColorHex}
                  isSelf={isSelf}
                />
              );
            })}
            {hoveredId
              ? (() => {
                  const territory = map.territories.find((t) => t.id === hoveredId);
                  if (!territory) return null;
                  const centroid = getOffsetCentroid(territory.id, territory.centroid);
                  return (
                    <g pointerEvents="none">
                      {/* Pin marker so the user sees exactly which dot they're on. */}
                      <circle
                        cx={centroid.x}
                        cy={centroid.y - 2}
                        r={3}
                        fill={surfaces.chalk}
                        fillOpacity={0.9}
                      />
                      {/* Label plate — cream paper tag with ink border */}
                      <rect
                        x={centroid.x - 80}
                        y={centroid.y - 52}
                        width={160}
                        height={24}
                        fill="rgba(245,236,211,0.96)"
                        stroke={surfaces.chalk}
                        strokeOpacity={0.7}
                        strokeWidth={0.8}
                        rx={2}
                      />
                      <text
                        x={centroid.x}
                        y={centroid.y - 35}
                        textAnchor="middle"
                        fill={surfaces.chalk}
                        fontSize={14}
                        letterSpacing="0.12em"
                        fontFamily="'Departure Mono', ui-monospace, monospace"
                      >
                        {territory.displayName.toUpperCase()}
                      </text>
                    </g>
                  );
                })()
              : null}
          </g>

          {/* Layer 5b — attack arrow(s). Moved AFTER badges to ensure visibility. */}
          {sourceTerritory && previewTargetTerritory ? (
            <AttackArrow
              key={`preview-${sourceTerritory.id}-${previewTargetTerritory.id}`}
              source={getOffsetCentroid(sourceTerritory.id, sourceTerritory.centroid)}
              target={getOffsetCentroid(previewTargetTerritory.id, previewTargetTerritory.centroid)}
              mode="preview"
            />
          ) : null}
          {sourceTerritory &&
          selectedTargetTerritory &&
          attackState.kind === 'targetSelected' ? (
            <AttackArrow
              key={`committed-${sourceTerritory.id}-${selectedTargetTerritory.id}`}
              source={getOffsetCentroid(sourceTerritory.id, sourceTerritory.centroid)}
              target={getOffsetCentroid(selectedTargetTerritory.id, selectedTargetTerritory.centroid)}
              mode="committed"
            />
          ) : null}

          {/* Layer 5c — Interactive Tutorial arrow hint (SVG only; the bubble
              is rendered as a DOM overlay via TutorialOverlay below) */}
          <InteractiveTutorial
            gameState={gameState}
            selfId={selfId}
            attackState={attackState}
            map={map}
          />

          <g data-layer="cube-deltas">
            {deltas.map((d) => {
              const rawCentroid = centroidById.get(d.territoryId as TerritoryId);
              if (!rawCentroid) return null;
              const centroid = getOffsetCentroid(d.territoryId as TerritoryId, rawCentroid);
              return (
                <CubeDelta
                  key={d.id}
                  territoryId={d.territoryId}
                  delta={d.delta}
                  centroid={centroid}
                  onComplete={() => dismiss(d.id)}
                />
              );
            })}
          </g>
        </svg>
      </TransformComponent>
    </TransformWrapper>

      {/* Floating attack-flow banner. Sibling to the TransformWrapper so
          it stays anchored at the top-center of the map area regardless
          of pan / zoom, and so it sits above the SVG in stacking order. */}
      <AttackBanner
        attackState={attackState}
        sourceName={sourceName}
        targetName={bannerTargetName}
      />

      {/* Tutorial overlay: rendered as a DOM element above the map so it
          never overlaps with territory SVG paths */}
      <TutorialOverlay
        gameState={gameState}
        selfId={selfId}
        attackState={attackState}
        map={map}
      />

      <ReservesHUD count={selfPlayerReserves} />
    </div>
  );
}

function ReservesHUD({ count }: { count: number }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  if (count <= 0) return null;

  // On mobile the 88px-tall fixed action bar (MobilePlayLayout.tsx) sits
  // along the bottom edge, so the HUD must clear it (88 + 12px gap = 100px)
  // and additionally lift above the iOS home-indicator via safe-area-inset.
  // On desktop the original 90px offset is preserved verbatim.
  const bottomOffset = isMobile
    ? 'calc(100px + env(safe-area-inset-bottom, 0px))'
    : 'calc(90px + env(safe-area-inset-bottom, 0px))';
  const leftOffset = 'calc(20px + env(safe-area-inset-left, 0px))';

  return (
    <div style={{ position: 'absolute', bottom: bottomOffset, left: leftOffset, zIndex: 10 }}>
      {/* Expandable info panel */}
      {open ? (
        <div
          style={{
            marginBottom: 8,
            background: '#0a1628',
            border: '1.5px solid #1d4ed8',
            borderRadius: 10,
            padding: '10px 14px',
            maxWidth: 230,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <p style={{ margin: '0 0 6px', fontFamily: fontStacks.mono, fontSize: 12, fontWeight: 800, color: '#ef4444', letterSpacing: '0.12em' }}>
            🎲 RESERVE CUBES
          </p>
          <p style={{ margin: '0 0 4px', fontFamily: fontStacks.mono, fontSize: 13, color: '#e2e8f0', lineHeight: 1.5 }}>
            You earn cubes each turn based on how many territories you control (min. 3 per turn).
          </p>
          <p style={{ margin: 0, fontFamily: fontStacks.mono, fontSize: 13, color: '#60a5fa', lineHeight: 1.5 }}>
            💡 When all your territories have only 1 cube and you can't attack, tap any territory to deploy a reserve cube there.
          </p>
        </div>
      ) : null}

      {/* Main pill */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          border: '2px solid rgba(255,255,255,0.3)',
          padding: '7px 16px',
          borderRadius: 22,
          boxShadow: '0 4px 18px rgba(29,78,216,0.55)',
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          whiteSpace: 'nowrap',
          animation: 'qd-reserves-pulse 2.5s ease-in-out infinite',
        }}
      >
        <svg width={16} height={16} viewBox="0 0 18 18" aria-hidden>
          <rect x={2} y={5} width={14} height={10} rx={2} fill="#fff" fillOpacity={0.9} stroke="#fff" strokeWidth={0.8} />
          <path d="M2 7 L9 4 L16 7" fill="none" stroke="#fff" strokeWidth={1.2} strokeLinejoin="round" />
          <path d="M9 4 L9 14" stroke="#fff" strokeWidth={0.8} strokeOpacity={0.5} />
        </svg>
        <span style={{ fontFamily: fontStacks.mono, fontSize: 15, fontWeight: 900, color: '#fff' }}>
          {count}
        </span>
        <span style={{ fontFamily: fontStacks.mono, fontSize: 10, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.08em' }}>
          RESERVE{count !== 1 ? 'S' : ''} ⓘ
        </span>
      </button>
      <style>{`
        @keyframes qd-reserves-pulse {
          0%, 100% { box-shadow: 0 4px 18px rgba(29,78,216,0.55); }
          50%       { box-shadow: 0 4px 28px rgba(29,78,216,0.85); }
        }
      `}</style>
    </div>
  );
}

interface TerritoryShapeProps {
  readonly territory: Territory;
  readonly fill: string;
  readonly fillOpacity: number;
  readonly isHovered: boolean;
  readonly isSource: boolean;
  readonly isValidTarget: boolean;
  /** True iff this territory is the locked-in `attackState.targetSelected.targetId`. */
  readonly isSelectedTarget: boolean;
  readonly onHover: (id: TerritoryId | null) => void;
  readonly onClick: (id: TerritoryId) => void;
}

function TerritoryShape({
  territory,
  fill,
  fillOpacity,
  isHovered,
  isSource,
  isValidTarget,
  isSelectedTarget,
  onHover,
  onClick,
}: TerritoryShapeProps): ReactNode {
  // Stroke per state. Source gets a much thicker line plus a glow halo
  // (rendered before the path) so it's unmistakable on any monitor.
  // The locked-in target gets the strongest border of all — solid (not
  // pulsing, because pulse implies "click me" and this one is already
  // chosen), thicker than the source ring, and rendered in pure wax-red
  // so it visually pairs with the chalk arrow drawn into it.
  const stroke = isSelectedTarget
    ? surfaces.waxRed
    : isSource
      ? surfaces.waxRed
      : isValidTarget
        ? surfaces.waxRed
        : surfaces.ink;
  const strokeWidth = isSelectedTarget
    ? 8
    : isSource
      ? 8
      : isValidTarget
        ? 6
        : isHovered
          ? 6
          : 4.5;

  // Source fill: blend the original fill with wax-pencil red so the
  // selected territory itself reads as "the picked one", not just an
  // outline. Valid targets get a faint wax tint to suggest "you can hit
  // this". The selected target gets the heaviest tint of all so it reads
  // as "this is where I'm going".
  const effectiveFillOpacity = isSelectedTarget
    ? Math.max(fillOpacity, 0.78)
    : isSource
      ? Math.max(fillOpacity, 0.7)
      : fillOpacity;

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={territory.displayName}
      onMouseEnter={() => onHover(territory.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(territory.id)}
      onBlur={() => onHover(null)}
      onClick={() => onClick(territory.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(territory.id);
        }
      }}
      style={{ cursor: 'crosshair', outline: 'none' }}
    >
      {/* Source GLOW halo — wide low-opacity wax-red stroke behind the
          territory so the selected one visibly "lights up". Pulses
          gently to draw the eye. */}
      {isSource ? (
        <path
          d={territory.pathD}
          fill="none"
          stroke={surfaces.waxRed}
          strokeWidth={14}
          strokeOpacity={0.45}
          style={{ pointerEvents: 'none' }}
        >
          <animate
            attributeName="stroke-opacity"
            values="0.5;0.18;0.5"
            dur="1.4s"
            repeatCount="indefinite"
          />
        </path>
      ) : null}

      {/* Selected-TARGET halo — a steady (NOT pulsing) thick wax-red
          ring. Pulse implies "click me to choose"; this territory is
          already chosen, so we want it to read as locked-in. Sits
          underneath everything else. */}
      {isSelectedTarget ? (
        <path
          d={territory.pathD}
          fill="none"
          stroke={surfaces.waxRed}
          strokeWidth={12}
          strokeOpacity={0.55}
          style={{ pointerEvents: 'none' }}
        />
      ) : null}

      {/* Valid-target halo — wider chalk-white halo so adjacent enemies
          stand out as clickable. Pulses faster than source. */}
      {isValidTarget ? (
        <path
          d={territory.pathD}
          fill="none"
          stroke={surfaces.paper}
          strokeWidth={9}
          strokeOpacity={0.55}
          style={{ pointerEvents: 'none' }}
        >
          <animate
            attributeName="stroke-opacity"
            values="0.65;0.18;0.65"
            dur="1.1s"
            repeatCount="indefinite"
          />
        </path>
      ) : null}

      {/* The territory itself. Source gets a tinted fill on top so the
          pigment shifts noticeably. */}
      <path
        d={territory.pathD}
        fill={fill}
        fillOpacity={effectiveFillOpacity}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeOpacity={isHovered ? 1 : 0.95}
        strokeLinejoin="round"
        filter="url(#qd-filter-geo-roughen)"
      />
      {isSource ? (
        <path
          d={territory.pathD}
          fill={surfaces.waxRed}
          fillOpacity={0.32}
          stroke="none"
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      {isValidTarget ? (
        <path
          d={territory.pathD}
          fill={surfaces.waxRed}
          fillOpacity={0.18}
          stroke="none"
          style={{ pointerEvents: 'none' }}
        />
      ) : null}
      {isSelectedTarget ? (
        <>
          {/* Heavy tint over the locked target. */}
          <path
            d={territory.pathD}
            fill={surfaces.waxRed}
            fillOpacity={0.4}
            stroke="none"
            style={{ pointerEvents: 'none' }}
          />
          {/* Chalk crosshair / target reticle, centered on the territory.
              Sits above the cube badge layer's territory but below the
              badge itself (which is rendered in a sibling <g> later). */}
          <TargetReticle cx={territory.centroid.x} cy={territory.centroid.y} />
        </>
      ) : null}
    </g>
  );
}

/**
 * Crosshair drawn on top of a locked-in attack target. Two short chalk
 * tick marks (N/S/E/W) framing a thin ring — same vocabulary as a
 * cartographer's range-finder reticle, intentionally simple so it doesn't
 * fight the cube badge that sits inside it.
 */
function TargetReticle({ cx, cy }: { cx: number; cy: number }): ReactNode {
  const tickInner = 16;
  const tickOuter = 22;
  const stroke = surfaces.waxRed;
  return (
    <g pointerEvents="none" data-layer="target-reticle">
      {/* Outer ring just outside the cube badge (badge r = 12). */}
      <circle
        cx={cx}
        cy={cy}
        r={18}
        fill="none"
        stroke={stroke}
        strokeOpacity={0.85}
        strokeWidth={1.6}
        strokeDasharray="3 2"
      />
      {/* N */}
      <line x1={cx} y1={cy - tickOuter} x2={cx} y2={cy - tickInner} stroke={stroke} strokeWidth={1.8} strokeOpacity={0.9} strokeLinecap="round" />
      {/* S */}
      <line x1={cx} y1={cy + tickInner} x2={cx} y2={cy + tickOuter} stroke={stroke} strokeWidth={1.8} strokeOpacity={0.9} strokeLinecap="round" />
      {/* E */}
      <line x1={cx + tickInner} y1={cy} x2={cx + tickOuter} y2={cy} stroke={stroke} strokeWidth={1.8} strokeOpacity={0.9} strokeLinecap="round" />
      {/* W */}
      <line x1={cx - tickOuter} y1={cy} x2={cx - tickInner} y2={cy} stroke={stroke} strokeWidth={1.8} strokeOpacity={0.9} strokeLinecap="round" />
    </g>
  );
}

function CubeBadge({
  cx, cy, count, ownerColor, isSelf,
}: {
  cx: number; cy: number; count: number;
  ownerColor?: string | null;
  isSelf?: boolean;
}): ReactNode {
  // Larger badges for better visibility, especially on mobile
  const r = isSelf ? 16 : 14;
  return (
    <g pointerEvents="none">
      {/* White fill so count is always legible */}
      <circle cx={cx} cy={cy} r={r} fill="#ffffff" fillOpacity={0.95} />
      {/* Owner color ring */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={ownerColor ?? '#1e293b'}
        strokeOpacity={1}
        strokeWidth={ownerColor ? 3 : 2}
      />
      {/* Count text — larger for visibility */}
      <text
        x={cx}
        y={cy + 5}
        textAnchor="middle"
        fill={ownerColor ?? '#0f172a'}
        fontSize={14}
        fontFamily="'Departure Mono', ui-monospace, monospace"
        fontWeight={800}
      >
        {count}
      </text>
      {/* Self-owned indicator: small dot above the badge */}
      {isSelf && ownerColor ? (
        <circle
          cx={cx}
          cy={cy - r - 4}
          r={3.5}
          fill={ownerColor}
          stroke="#fff"
          strokeWidth={1.2}
        />
      ) : null}
    </g>
  );
}

function InteractiveTutorial({ gameState, selfId, attackState, map }: any) {
  const [show, setShow] = useState(false);
  const [hint, setHint] = useState<{sourceId: any, source: any, targetId: any, target: any} | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem('qd-interactive-tutorial') === '1') return;
    
    if (!gameState || !selfId) return;
    const isMyTurn = gameState.playerOrder[gameState.currentPlayerIdx] === selfId;
    
    if (isMyTurn && attackState.kind === 'idle') {
      setShow(true);
      const timer = setTimeout(() => {
        const myTerritories = (Object.values(gameState.territories) as any[]).filter((t: any) => t.controllingPlayerId === selfId && t.cubeCount >= 2);
        for (const t of myTerritories) {
          const mapTerritory = map.territories.find((x: any) => x.id === t.territoryId);
          if (!mapTerritory) continue;
          for (const adjId of mapTerritory.adjacencyIds) {
            const enemy = gameState.territories[adjId];
            if (enemy && enemy.controllingPlayerId !== selfId) {
              const tgtShape = map.territories.find((x: any) => x.id === adjId);
              if (tgtShape) {
                setHint({ sourceId: mapTerritory.id, source: mapTerritory.centroid, targetId: tgtShape.id, target: tgtShape.centroid });
                return;
              }
            }
          }
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
    
    if (attackState.kind !== 'idle' && isMyTurn) {
      window.localStorage.setItem('qd-interactive-tutorial', '1');
      setShow(false);
      setHint(null);
    }
  }, [gameState, selfId, attackState.kind, map]);

  if (!show) return null;

  // Render as a fixed DOM overlay outside the SVG so it never collides
  // with continent territories (the foreignObject approach placed it
  // at SVG coordinates x=650,y=20 which maps directly over the top-right
  // continent area at most zoom levels).
  return (
    <>
      {hint ? (
        <g data-layer="tutorial" style={{ pointerEvents: 'none' }}>
          <AttackArrow source={getOffsetCentroid(hint.sourceId, hint.source)} target={getOffsetCentroid(hint.targetId, hint.target)} mode="preview" />
        </g>
      ) : null}
    </>
  );
}

/** Tutorial overlay rendered as a fixed DOM element, mounted from MapView. */
export function TutorialOverlay({ gameState, selfId, attackState, map }: any) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem('qd-interactive-tutorial') === '1') return;
    if (!gameState || !selfId) return;
    const isMyTurn = gameState.playerOrder[gameState.currentPlayerIdx] === selfId;
    if (isMyTurn && attackState.kind === 'idle') {
      setShow(true);
    }
    if (attackState.kind !== 'idle' && isMyTurn) {
      window.localStorage.setItem('qd-interactive-tutorial', '1');
      setShow(false);
    }
  }, [gameState, selfId, attackState.kind, map]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'absolute',
        // Anchor to top-center of the map area, well clear of continents.
        // Pad for iOS notch / Dynamic Island so the bubble isn't clipped
        // by the system UI on phones.
        top: 'calc(12px + env(safe-area-inset-top, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        // z-index 30: above the MobileDashboardSheet (z-index 20) so the
        // tutorial bubble isn't covered when the sheet is open, but well
        // below the combat/quiz modal (~100).
        zIndex: 30,
        pointerEvents: 'auto',
        animation: 'qd-bounce 2s infinite',
      }}
    >
      <div
        style={{
          background: 'rgba(10, 17, 35, 0.92)',
          color: '#f1f5f9',
          padding: '10px 18px',
          borderRadius: 20,
          fontFamily: fontStacks.display,
          fontWeight: 700,
          fontSize: 14,
          lineHeight: 1.35,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          border: `2px solid #ef4444`,
          backdropFilter: 'blur(8px)',
          position: 'relative',
          maxWidth: 'min(86vw, 460px)',
          textAlign: 'center',
          wordBreak: 'break-word',
        }}
      >
        <button
          aria-label="Close tutorial"
          onClick={(e) => {
            e.stopPropagation();
            window.localStorage.setItem('qd-interactive-tutorial', '1');
            setShow(false);
          }}
          style={{
            position: 'absolute', top: -10, right: -10, width: 24, height: 24,
            background: '#ef4444', color: '#fff', borderRadius: '50%',
            border: 'none', cursor: 'pointer', fontWeight: 'bold',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)', fontSize: 12, padding: 0
          }}
        >
          ✕
        </button>
        🎯 Click one of YOUR territories (2+ cubes) to start an attack!
      </div>
      <style>{`
        @keyframes qd-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
